<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import { EventDefine } from '@/defines/EventDefine'
import TurnTableButton from '@/components/TurnTableButton.vue'

const props = defineProps({
  activeTab: {
    type: String,
    default: 'Casino',
  },
  hideBottomNav: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:activeTab', 'open-sidebar', 'open-pinduo'])

const balance = ref(hall.userModule.gold)

const updateBalance = () => {
  balance.value = hall.userModule.gold
}

const onChangeBindActivityRed = () => {
  hall.bindActivityModule.changeRedStatus()
}

const getTabIcon = (tab: string) => {
  const isActive = props.activeTab === tab
  const iconMap: Record<string, { on: string; off: string }> = {
    Casino: { on: 'tab01_casino01.webp', off: 'tab01_casino02.webp' },
    Refer: { on: 'tab01_refer01.webp', off: 'tab01_refer02.webp' },
    Deposit: { on: 'tab01_deposit01.webp', off: 'tab01_deposit02.webp' },
    Account: { on: 'tab01_account01.webp', off: 'tab01_account02.webp' },
  }
  const entry = iconMap[tab]
  if (!entry) return ''
  const icon = isActive ? entry.on : entry.off
  return new URL(`../assets/hall/hallTop/${icon}`, import.meta.url).href
}

onMounted(() => {
  hall.eventDispatcher.on(EventDefine.MoneyChange, updateBalance)
  hall.eventDispatcher.on(EventDefine.ChangeBindActivityRed, onChangeBindActivityRed)
})

onUnmounted(() => {
  hall.eventDispatcher.off(EventDefine.MoneyChange, updateBalance)
  hall.eventDispatcher.off(EventDefine.ChangeBindActivityRed, onChangeBindActivityRed)
})
</script>

<template>
  <div class="lobby-container">
    <!-- ===== TOP HEADER ===== -->
    <header class="lobby-header">
      <!-- Mobile: hamburger menu -->
      <div class="menu-btn mobile-only" @click="emit('open-sidebar')">
        <img src="@/assets/hall/hallTop/btn01_caidan.webp" alt="Menu" class="menu-icon-img" />
      </div>

      <!-- PC: Logo area (left slot of header) -->
      <div class="pc-logo desktop-only">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="var(--theme-accent)">
          <path
            d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm7 14l7-7-1.4-1.4L12 14.2l-3.6-3.6L7 12l5 5z"
          />
        </svg>
        <span class="logo-text">GAME HALL</span>
      </div>

      <!-- Dynamic Title / Balance Area -->
      <div v-if="props.title" class="header-title-area">
        <button class="back-btn header-icon" @click="emit('open-pinduo', false)">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path
              fill="currentColor"
              d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6l6 6l1.41-1.41z"
            />
          </svg>
        </button>
        <span class="title-text">{{ props.title }}</span>
      </div>

      <div v-else class="header-center-wrap">
        <!-- Balance / Add Funds -->
        <div class="user-balance cursor-pointer" @click="emit('update:activeTab', 'Deposit')">
          <div class="balance-display position-relative">
            <img src="@/assets/hall/hallTop/icon02_gold01.webp" class="coin-icon" alt="Gold" />
            <span class="balance-amount">{{ balance }}</span>
            <img
              src="@/assets/hall/hallTop/btn04_charge01.webp"
              class="add-funds-btn"
              alt="Charge"
            />
          </div>
        </div>
      </div>

      <div class="header-actions">
        <div class="wallet-btn">
          <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
            <path d="M21 7H3V5h18v2zm0 4H5v2h16v-2zm-2 4H7v2h12v-2z" />
          </svg>
        </div>
        <div class="support-btn">
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            />
          </svg>
        </div>
      </div>
    </header>

    <!-- ===== PC LEFT SIDEBAR ===== -->
    <nav class="pc-sidebar desktop-only" v-if="!hideBottomNav">
      <!-- Casino -->
      <div
        class="sidebar-item"
        :class="{ active: activeTab === 'Casino' }"
        @click="emit('update:activeTab', 'Casino')"
        title="Casino"
      >
        <img :src="getTabIcon('Casino')" class="sidebar-icon-img" />
        <span class="sidebar-label">Casino</span>
      </div>

      <!-- Refer -->
      <div
        class="sidebar-item"
        :class="{ active: activeTab === 'Refer' }"
        @click="emit('update:activeTab', 'Refer')"
        title="Refer & Earn"
      >
        <img :src="getTabIcon('Refer')" class="sidebar-icon-img" />
        <span class="sidebar-label">Refer</span>
      </div>

      <!-- PinDuo / Wheel - special center item -->
      <div class="sidebar-item sidebar-wheel" @click="emit('open-pinduo')" title="Free Spin">
        <div class="sidebar-wheel-wrap">
          <TurnTableButton :active="activeTab === 'Wheel'" />
        </div>
        <span class="sidebar-label">Free</span>
      </div>

      <!-- Deposit -->
      <div
        class="sidebar-item"
        :class="{ active: activeTab === 'Deposit' }"
        @click="emit('update:activeTab', 'Deposit')"
        title="Deposit"
      >
        <img :src="getTabIcon('Deposit')" class="sidebar-icon-img" />
        <span class="sidebar-label">Deposit</span>
      </div>

      <!-- Account -->
      <div
        class="sidebar-item"
        :class="{ active: activeTab === 'Account' }"
        @click="emit('update:activeTab', 'Account')"
        title="Account"
      >
        <img :src="getTabIcon('Account')" class="sidebar-icon-img" />
        <span class="sidebar-label">Account</span>
      </div>
    </nav>

    <!-- ===== MAIN CONTENT ===== -->
    <main class="lobby-content">
      <slot />
      <!-- bottom spacer: mobile only (desktop sidebar doesn't need it) -->
      <div v-if="!hideBottomNav" class="bottom-spacer mobile-spacer"></div>
    </main>

    <!-- ===== MOBILE BOTTOM NAV (unchanged) ===== -->
    <nav v-if="!hideBottomNav" class="bottom-nav mobile-only">
      <div
        class="nav-item"
        :class="{ active: props.activeTab === 'Casino' }"
        @click="emit('update:activeTab', 'Casino')"
      >
        <img :src="getTabIcon('Casino')" class="nav-icon" />
        <span>Casino</span>
      </div>

      <div
        class="nav-item"
        :class="{ active: props.activeTab === 'Refer' }"
        @click="emit('update:activeTab', 'Refer')"
      >
        <img :src="getTabIcon('Refer')" class="nav-icon" />
        <span>Refer&Earn</span>
      </div>

      <div class="nav-item wheel-center" @click="emit('open-pinduo')">
        <TurnTableButton :active="props.activeTab === 'Wheel'" />
      </div>

      <div
        class="nav-item"
        :class="{ active: props.activeTab === 'Deposit' }"
        @click="emit('update:activeTab', 'Deposit')"
      >
        <img :src="getTabIcon('Deposit')" class="nav-icon" />
        <span>Deposit</span>
      </div>

      <div
        class="nav-item"
        :class="{ active: props.activeTab === 'Account' }"
        @click="emit('update:activeTab', 'Account')"
      >
        <img :src="getTabIcon('Account')" class="nav-icon" />
        <span>Account</span>
      </div>
    </nav>
  </div>
</template>

<style scoped>
/* ===================== SHARED BASE ===================== */
.lobby-container {
  width: 100%;
  min-height: 100vh;
  background-image: url('@/assets/hall/background.webp');
  background-size: cover;
  background-position: center top;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', sans-serif;
  color: white;
  position: relative;
  overflow-x: hidden;
}

/* Header */
.lobby-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  z-index: 1000;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.menu-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.menu-icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.balance-display {
  display: flex;
  align-items: center;
  background: var(--theme-lobby-bg);
  border: 1px solid var(--theme-border-card);
  border-radius: 20px;
  height: 36px;
  padding: 0 4px;
}

.coin-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  object-fit: contain;
}

.balance-amount {
  font-weight: bold;
  color: #fff;
  font-size: 0.95rem;
  margin-right: 4px;
}

.add-funds-btn {
  width: 32px;
  height: 32px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s;
}

.add-funds-btn:active {
  transform: scale(0.9);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.wallet-btn {
  background: var(--theme-active-gradient);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
}

.support-btn {
  color: var(--theme-accent);
  cursor: pointer;
}

/* Main Content */
.lobby-content {
  flex: 1;
  overflow-y: auto;
  padding-top: 56px;
  padding-bottom: 0px;
}

.header-title-area {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-btn {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
}

.title-text {
  color: #fff;
  font-size: 1.1rem;
  font-weight: bold;
}

/* ===================== MOBILE ONLY ===================== */
/* Mobile bottom spacer */
.mobile-spacer {
  height: 90px;
}

/* Bottom Nav */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 65px;
  background: var(--theme-nav-bg);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  padding-bottom: 8px;
  z-index: 1000;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #888;
  font-size: 0.7rem;
  gap: 4px;
  flex: 1;
  cursor: pointer;
}

.nav-item.active {
  color: var(--theme-accent);
}

.wheel-center {
  position: relative;
  flex: 1.5;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 12px;
}

.nav-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.active .nav-icon {
  width: 32px;
  height: 32px;
}

/* ===================== PC / DESKTOP SIDEBAR ===================== */
.pc-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px; /* match sidebar width */
  padding-left: 4px;
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.logo-text {
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 1px;
  background: var(--theme-btn-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pc-sidebar {
  position: fixed;
  top: 56px;
  left: 0;
  width: 80px;
  height: calc(100vh - 56px);
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0;
  gap: 4px;
  z-index: 900;
  overflow-y: auto;
  scrollbar-width: none;
}

.pc-sidebar::-webkit-scrollbar {
  display: none;
}

.sidebar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 72px;
  padding: 10px 4px;
  border-radius: 10px;
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;
  color: #888;
}

.sidebar-item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #ccc;
}

.sidebar-item.active {
  background: var(--theme-bg-card);
  color: var(--theme-accent);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar-icon-img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.sidebar-label {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  text-align: center;
  line-height: 1;
}

.sidebar-wheel {
  padding: 6px 4px;
}

.sidebar-wheel-wrap {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0.7);
  transform-origin: center;
}

/* ===================== RESPONSIVE BREAKPOINTS ===================== */

/* Mobile: show bottom nav, hide PC sidebar & logo */
@media (max-width: 767px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex !important;
  }
}

/* Desktop: hide bottom nav & mobile elements, show sidebar */
@media (min-width: 768px) {
  .mobile-only {
    display: none !important;
  }

  .desktop-only {
    display: flex !important;
  }

  /* Header left area gets wider to accommodate logo */
  .lobby-header {
    padding: 0 20px 0 0;
  }

  /* Push content right to make room for sidebar, add breathing room on all edges */
  .lobby-content {
    padding-left: calc(80px + 60px);
    padding-right: 100px;
    padding-bottom: 12px;
  }

  /* No bottom spacer needed on desktop */
  .mobile-spacer {
    display: none;
  }
}
</style>
